/* eslint-disable react/jsx-key */
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import React from 'react';
import CodeBlock from '@dashboard/components/CodeBlock';
import DisclosureCard from '@dashboard/components/DisclosureCard';
import { overlayUrl } from '@dashboard/services/env';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import CheckoutShareWebsite from './CheckoutShareWebsite';
import getEmbedCode from '@/dashboard/lib/getEmbedCode';
import getPopupCode from '@/dashboard/lib/getPopupCode';
import CheckoutShareQRCode from './CheckoutShareQRCode';

const CheckoutShareMethods = ({ page, seller }) => {
  const checkoutUrl = getCheckoutUrl(page, seller);

  const embedInstructions = getSimplifiedEmbedInstructions({
    page,
    seller,
    overlayUrl,
    checkoutUrl,
  });

  return (
    <div className="border-t border-gray-200">
      {embedInstructions.map(instructions => (
        <DisclosureCard className="border-b border-gray-200 py-2" key={instructions.key}>
          <DisclosureCard.Button>{instructions.name}</DisclosureCard.Button>
          <DisclosureCard.Panel>
            {instructions.components?.length > 0 && (
              <div className="space-y-6 mb-4">
                {instructions.components.map((c, i) => (
                  <React.Fragment key={instructions.key + i}>{c}</React.Fragment>
                ))}
              </div>
            )}
          </DisclosureCard.Panel>
        </DisclosureCard>
      ))}
    </div>
  );
};

function StepByStepInstructionsLink({ instructionsLink }) {
  return (
    <div className="pt-2 font-medium text-sm underline">
      <a href={instructionsLink} target="_blank" className="flex" rel="noreferrer">
        Read step-by-step instructions
        <ArrowTopRightOnSquareIcon className="ml-1 w-5 h-5 text-gray-600" />
      </a>
    </div>
  );
}

function StepByStepInstructions({ instructions }) {
  return <div className="text-sm py-4">{instructions}</div>;
}

const tabs = ({ embedCode, popUpCode, scriptCode }) => [
  {
    key: 'embed',
    name: 'Responsive embed',
    content: (
      <div className="pt-4 rounded-md">
        <CodeBlock copy>{embedCode}</CodeBlock>

        <StepByStepInstructions instructions={<>1. Add the embed code to your website</>} />
        <StepByStepInstructionsLink instructionsLink="/help/articles/embed-your-checkout-on-your-website" />
      </div>
    ),
  },
  {
    key: 'popup',
    name: 'Checkout pop-up',
    content: (
      <div className="pt-4 rounded-md">
        <CodeBlock copy>{scriptCode}</CodeBlock>

        <StepByStepInstructions
          instructions={
            <>
              1. Add this script to the <b>header</b> of your website
            </>
          }
        />
        <CodeBlock copy>{popUpCode}</CodeBlock>
        <StepByStepInstructions
          instructions={
            <>
              2. Add this URL to a <b>link</b> or <b>button</b>. Make sure it has the class{' '}
              <b>cp-button</b>
            </>
          }
        />
        <StepByStepInstructionsLink instructionsLink="/help/articles/add-a-pop-up-checkout-to-your-site" />
      </div>
    ),
  },
];

function getSimplifiedEmbedInstructions({ page, seller, checkoutUrl, overlayUrl }) {
  const { scriptCode, popUpCode } = getPopupCode({ checkoutUrl, overlayUrl });
  const embedCode = getEmbedCode({ checkoutUrl, overlayUrl });

  return [
    {
      key: 'custom',
      name: 'Add to website',
      components: [
        <CheckoutShareWebsite
          tabs={tabs({
            embedCode,
            popUpCode,
            scriptCode,
          })}
        />,
      ],
    },
    {
      key: 'qrcode',
      name: 'QR code',
      components: [<CheckoutShareQRCode page={page} seller={seller}></CheckoutShareQRCode>],
    },
  ];
}

export default CheckoutShareMethods;
